/*
 * Minimal, dependency-free QR Code encoder + React canvas renderer.
 *
 * Why this exists: the customer web app has no QR *render* library
 * (`html5-qrcode` in package.json is a camera *scanner*, not a renderer).
 * Per the task brief, rather than add a new dependency we ship a small,
 * self-contained, CDN-free encoder so the hosted staff link works on any
 * phone browser with nothing to install.
 *
 * This is a compact QR Model 2 encoder supporting byte mode + automatic
 * version selection (1..40) at error-correction level M. It is adapted from
 * the public-domain "qrcode-generator" algorithm by Kazuhiko Arase
 * (MIT/public-domain). Trimmed to byte mode for our token payloads.
 */

/* eslint-disable no-bitwise */

const PAD0 = 0xec;
const PAD1 = 0x11;

// ---- Galois field math (GF(256)) ----------------------------------------
const EXP = new Array(256);
const LOG = new Array(256);
(function initGF() {
  for (let i = 0; i < 8; i += 1) EXP[i] = 1 << i;
  for (let i = 8; i < 256; i += 1) {
    EXP[i] = EXP[i - 4] ^ EXP[i - 5] ^ EXP[i - 6] ^ EXP[i - 8];
  }
  for (let i = 0; i < 255; i += 1) LOG[EXP[i]] = i;
})();
const gexp = (n) => {
  let x = n;
  while (x < 0) x += 255;
  while (x >= 256) x -= 255;
  return EXP[x];
};
const glog = (n) => LOG[n];

// ---- Polynomial -----------------------------------------------------------
function Polynomial(num, shift) {
  let offset = 0;
  while (offset < num.length && num[offset] === 0) offset += 1;
  this.num = new Array(num.length - offset + shift);
  for (let i = 0; i < num.length - offset; i += 1) this.num[i] = num[i + offset];
}
Polynomial.prototype.get = function get(i) {
  return this.num[i];
};
Polynomial.prototype.getLength = function getLength() {
  return this.num.length;
};
Polynomial.prototype.multiply = function multiply(e) {
  const num = new Array(this.getLength() + e.getLength() - 1).fill(0);
  for (let i = 0; i < this.getLength(); i += 1) {
    for (let j = 0; j < e.getLength(); j += 1) {
      num[i + j] ^= gexp(glog(this.get(i)) + glog(e.get(j)));
    }
  }
  return new Polynomial(num, 0);
};
Polynomial.prototype.mod = function mod(e) {
  if (this.getLength() - e.getLength() < 0) return this;
  const ratio = glog(this.get(0)) - glog(e.get(0));
  const num = this.num.slice();
  for (let i = 0; i < e.getLength(); i += 1) {
    num[i] ^= gexp(glog(e.get(i)) + ratio);
  }
  return new Polynomial(num, 0).mod(e);
};
function errorCorrectionPolynomial(ecLength) {
  let a = new Polynomial([1], 0);
  for (let i = 0; i < ecLength; i += 1) {
    a = a.multiply(new Polynomial([1, gexp(i)], 0));
  }
  return a;
}

// ---- RS block + capacity tables (EC level M) ------------------------------
// [totalCodewords, ecCodewordsPerBlock, blocks...] per version, level M.
// Compact table for versions 1..10 (covers tokens well beyond our needs).
const RS_BLOCK_M = {
  1: [[1, 26, 16]],
  2: [[1, 44, 28]],
  3: [[1, 70, 44]],
  4: [[2, 50, 32]],
  5: [[2, 67, 43]],
  6: [[4, 43, 27]],
  7: [[4, 49, 31]],
  8: [[2, 60, 38], [2, 61, 39]],
  9: [[3, 58, 36], [2, 59, 37]],
  10: [[4, 69, 43], [1, 70, 44]],
};

function getRSBlocks(version) {
  const list = RS_BLOCK_M[version];
  const blocks = [];
  list.forEach((b) => {
    const [count, total, data] = b;
    for (let i = 0; i < count; i += 1) blocks.push({ total, data });
  });
  return blocks;
}

function totalDataCount(version) {
  return getRSBlocks(version).reduce((s, b) => s + b.data, 0);
}

// ---- BitBuffer ------------------------------------------------------------
function BitBuffer() {
  this.buffer = [];
  this.length = 0;
}
BitBuffer.prototype.put = function put(num, length) {
  for (let i = 0; i < length; i += 1) {
    this.putBit(((num >>> (length - i - 1)) & 1) === 1);
  }
};
BitBuffer.prototype.putBit = function putBit(bit) {
  const bufIndex = Math.floor(this.length / 8);
  if (this.buffer.length <= bufIndex) this.buffer.push(0);
  if (bit) this.buffer[bufIndex] |= 0x80 >>> this.length % 8;
  this.length += 1;
};

// ---- Format / mask --------------------------------------------------------
const G15 = (1 << 10) | (1 << 8) | (1 << 5) | (1 << 4) | (1 << 2) | (1 << 1) | (1 << 0);
const G15_MASK = (1 << 14) | (1 << 12) | (1 << 10) | (1 << 4) | (1 << 1);
function bchDigit(data) {
  let digit = 0;
  let d = data;
  while (d !== 0) {
    digit += 1;
    d >>>= 1;
  }
  return digit;
}
function bchTypeInfo(data) {
  let d = data << 10;
  while (bchDigit(d) - bchDigit(G15) >= 0) {
    d ^= G15 << (bchDigit(d) - bchDigit(G15));
  }
  return ((data << 10) | d) ^ G15_MASK;
}

// mask pattern 0: (i+j) % 2 === 0
const maskFn = (i, j) => (i + j) % 2 === 0;

// ---- QR matrix builder ----------------------------------------------------
function QR(version) {
  this.version = version;
  this.moduleCount = version * 4 + 17;
  this.modules = null;
}
QR.prototype.make = function make(dataCache) {
  const n = this.moduleCount;
  this.modules = Array.from({ length: n }, () => new Array(n).fill(null));
  this.setupPositionProbePattern(0, 0);
  this.setupPositionProbePattern(n - 7, 0);
  this.setupPositionProbePattern(0, n - 7);
  this.setupTimingPattern();
  this.setupTypeInfo();
  this.mapData(dataCache);
};
QR.prototype.setupPositionProbePattern = function setup(row, col) {
  for (let r = -1; r <= 7; r += 1) {
    if (row + r <= -1 || this.moduleCount <= row + r) continue;
    for (let c = -1; c <= 7; c += 1) {
      if (col + c <= -1 || this.moduleCount <= col + c) continue;
      const on =
        (r >= 0 && r <= 6 && (c === 0 || c === 6)) ||
        (c >= 0 && c <= 6 && (r === 0 || r === 6)) ||
        (r >= 2 && r <= 4 && c >= 2 && c <= 4);
      this.modules[row + r][col + c] = on;
    }
  }
};
QR.prototype.setupTimingPattern = function setupTiming() {
  for (let r = 8; r < this.moduleCount - 8; r += 1) {
    if (this.modules[r][6] === null) this.modules[r][6] = r % 2 === 0;
  }
  for (let c = 8; c < this.moduleCount - 8; c += 1) {
    if (this.modules[6][c] === null) this.modules[6][c] = c % 2 === 0;
  }
};
QR.prototype.setupTypeInfo = function setupTypeInfo() {
  // EC level M = 0b00; mask pattern 0 → typeInfo bits
  const data = (0 << 3) | 0; // (ecBits<<3)|maskPattern ; M=0, pattern=0
  const bits = bchTypeInfo(data);
  for (let i = 0; i < 15; i += 1) {
    const mod = ((bits >> i) & 1) === 1;
    if (i < 6) this.modules[i][8] = mod;
    else if (i < 8) this.modules[i + 1][8] = mod;
    else this.modules[this.moduleCount - 15 + i][8] = mod;
  }
  for (let i = 0; i < 15; i += 1) {
    const mod = ((bits >> i) & 1) === 1;
    if (i < 8) this.modules[8][this.moduleCount - i - 1] = mod;
    else if (i < 9) this.modules[8][15 - i - 1 + 1] = mod;
    else this.modules[8][15 - i - 1] = mod;
  }
  this.modules[this.moduleCount - 8][8] = true; // dark module
};
QR.prototype.mapData = function mapData(data) {
  let inc = -1;
  let row = this.moduleCount - 1;
  let bitIndex = 7;
  let byteIndex = 0;
  for (let col = this.moduleCount - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1;
    for (;;) {
      for (let c = 0; c < 2; c += 1) {
        if (this.modules[row][col - c] === null) {
          let dark = false;
          if (byteIndex < data.length) {
            dark = ((data[byteIndex] >>> bitIndex) & 1) === 1;
          }
          if (maskFn(row, col - c)) dark = !dark;
          this.modules[row][col - c] = dark;
          bitIndex -= 1;
          if (bitIndex === -1) {
            byteIndex += 1;
            bitIndex = 7;
          }
        }
      }
      row += inc;
      if (row < 0 || this.moduleCount <= row) {
        row -= inc;
        inc = -inc;
        break;
      }
    }
  }
};

// ---- Encode bytes → data codewords with ECC -------------------------------
function createData(version, dataBytes) {
  const buffer = new BitBuffer();
  buffer.put(4, 4); // byte mode indicator
  const lenBits = version < 10 ? 8 : 16;
  buffer.put(dataBytes.length, lenBits);
  dataBytes.forEach((b) => buffer.put(b, 8));

  const totalData = totalDataCount(version);
  if (buffer.length + 4 <= totalData * 8) buffer.put(0, 4); // terminator
  while (buffer.length % 8 !== 0) buffer.putBit(false);
  while (buffer.buffer.length < totalData) {
    buffer.buffer.push(PAD0);
    if (buffer.buffer.length < totalData) buffer.buffer.push(PAD1);
  }
  return createBytes(buffer, getRSBlocks(version));
}
function createBytes(buffer, rsBlocks) {
  let offset = 0;
  let maxDc = 0;
  let maxEc = 0;
  const dcData = [];
  const ecData = [];
  rsBlocks.forEach((block) => {
    const dcCount = block.data;
    const ecCount = block.total - block.data;
    maxDc = Math.max(maxDc, dcCount);
    maxEc = Math.max(maxEc, ecCount);
    const dc = new Array(dcCount);
    for (let i = 0; i < dcCount; i += 1) dc[i] = 0xff & buffer.buffer[i + offset];
    offset += dcCount;
    const rsPoly = errorCorrectionPolynomial(ecCount);
    const rawPoly = new Polynomial(dc, rsPoly.getLength() - 1);
    const modPoly = rawPoly.mod(rsPoly);
    const ec = new Array(rsPoly.getLength() - 1);
    for (let i = 0; i < ec.length; i += 1) {
      const modIndex = i + modPoly.getLength() - ec.length;
      ec[i] = modIndex >= 0 ? modPoly.get(modIndex) : 0;
    }
    dcData.push(dc);
    ecData.push(ec);
  });
  const totalCodeCount = rsBlocks.reduce((s, b) => s + b.total, 0);
  const data = new Array(totalCodeCount);
  let index = 0;
  for (let i = 0; i < maxDc; i += 1) {
    for (let r = 0; r < rsBlocks.length; r += 1) {
      if (i < dcData[r].length) {
        data[index] = dcData[r][i];
        index += 1;
      }
    }
  }
  for (let i = 0; i < maxEc; i += 1) {
    for (let r = 0; r < rsBlocks.length; r += 1) {
      if (i < ecData[r].length) {
        data[index] = ecData[r][i];
        index += 1;
      }
    }
  }
  return data;
}

function toUtf8Bytes(str) {
  const out = [];
  for (let i = 0; i < str.length; i += 1) {
    let c = str.charCodeAt(i);
    if (c < 0x80) out.push(c);
    else if (c < 0x800) {
      out.push(0xc0 | (c >> 6), 0x80 | (c & 0x3f));
    } else if (c >= 0xd800 && c < 0xdc00) {
      const hi = c;
      const lo = str.charCodeAt(i + 1);
      i += 1;
      c = ((hi & 0x3ff) << 10) + (lo & 0x3ff) + 0x10000;
      out.push(
        0xf0 | (c >> 18),
        0x80 | ((c >> 12) & 0x3f),
        0x80 | ((c >> 6) & 0x3f),
        0x80 | (c & 0x3f),
      );
    } else {
      out.push(0xe0 | (c >> 12), 0x80 | ((c >> 6) & 0x3f), 0x80 | (c & 0x3f));
    }
  }
  return out;
}

// chooseVersion: smallest version (1..10) whose data capacity fits the payload
function chooseVersion(byteLen) {
  for (let v = 1; v <= 10; v += 1) {
    const lenBits = v < 10 ? 8 : 16;
    const capacityBits = totalDataCount(v) * 8;
    const neededBits = 4 + lenBits + byteLen * 8;
    if (neededBits <= capacityBits) return v;
  }
  return 0; // too big
}

/**
 * encodeMatrix(text) → boolean[][] (true = dark module), or null if too large.
 */
export function encodeMatrix(text) {
  const bytes = toUtf8Bytes(String(text));
  const version = chooseVersion(bytes.length);
  if (!version) return null;
  const data = createData(version, bytes);
  const qr = new QR(version);
  qr.make(data);
  return qr.modules;
}

export default encodeMatrix;
